import React, { ReactNode } from 'react';

import { usePermissions } from '../../hooks/use-permissions';

interface PermissionGuardProps {
  children: ReactNode;
  permission?: string;
  permissions?: string[];
  role?: string;
  roles?: string[];
  requireAll?: boolean;
  fallback?: ReactNode;
}

function PermissionGuard({
  children,
  permission,
  permissions,
  role,
  roles,
  requireAll,
  fallback,
}: PermissionGuardProps) {
  const { hasPermission, hasRole, hasAnyPermission, hasAnyRole, hasAllPermissions, hasAllRoles } = usePermissions();

  let hasAccess = true;

  // Check single permission
  if (permission && !hasPermission(permission)) {
    hasAccess = false;
  }

  // Check multiple permissions
  if (permissions && permissions.length > 0) {
    if (requireAll) {
      hasAccess = hasAccess && hasAllPermissions(permissions);
    } else {
      hasAccess = hasAccess && hasAnyPermission(permissions);
    }
  }

  // Check single role
  if (role && !hasRole(role)) {
    hasAccess = false;
  }

  // Check multiple roles
  if (roles && roles.length > 0) {
    if (requireAll) {
      hasAccess = hasAccess && hasAllRoles(roles);
    } else {
      hasAccess = hasAccess && hasAnyRole(roles);
    }
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return hasAccess ? <>{children}</> : <>{fallback}</>;
}

PermissionGuard.defaultProps = {
  permission: undefined,
  permissions: undefined,
  role: undefined,
  roles: undefined,
  requireAll: false,
  fallback: null,
};

export default PermissionGuard;
